import React, { useState, useEffect } from 'react'
import { hsclLogo2, agoraCoreNew } from '../../assets/images'
import { Link, useNavigate } from 'react-router-dom'
import {
    IconUsersGroup,
    IconClock,
    IconChartBar,
    IconCreditCard,
    IconChevronLeft,
    IconHome,
    IconError404
} from '@tabler/icons-react'

export default function Page404() {
    const navigate = useNavigate();
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return (
        <div className='flex h-svh overflow-hidden bg-white'>
            {/* Left Side: Brand & Features (40%) */}
            <div className='hidden lg:flex lg:w-[40%] flex-col bg-primary p-16 xl:p-24 text-white relative overflow-hidden'>
                {/* Mesh Glow Blobs */}
                <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-white/10 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-white/5 rounded-full blur-[80px]"></div>

                {/* Logo top-left */}
                <div className={`mb-20 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                    <img src={agoraCoreNew} alt='AGORA' className='h-24 w-auto object-contain opacity-90 brightness-110' />
                </div>

                <div className='flex-1 flex flex-col justify-center relative z-10'>
                    <h2 className={`text-4xl xl:text-5xl font-bold mb-16 tracking-tight transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Hi, Welcome back
                    </h2>

                    <div className='space-y-10'>
                        {[
                            { icon: IconUsersGroup, title: 'Comprehensive HR Management', desc: 'Streamline employee data, departments, and attendance tracking in one central hub.' },
                            { icon: IconClock, title: 'Leave & Time Tracking', desc: 'Effortlessly manage leave requests, balances, and detailed timesheets for your workforce.' },
                            { icon: IconChartBar, title: 'Performance & KPI Insights', desc: 'Monitor organizational growth with intuitive KPI dashboards and performance history tracking.' },
                            { icon: IconCreditCard, title: 'Financial & Accounts Control', desc: 'Stay on top of your bottom line with integrated management of payments, expenses, and invoices.' }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`flex items-start gap-6 group transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${100 + (idx * 150)}ms` }}
                            >
                                <div className='p-3.5 bg-white/10 rounded-2xl group-hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/5'>
                                    <item.icon size={26} className='text-white' />
                                </div>
                                <div className='pt-1'>
                                    <h3 className='text-lg xl:text-xl font-bold mb-1.5 group-hover:text-white transition-colors'>{item.title}</h3>
                                    <p className='text-white/70 text-sm leading-relaxed max-w-sm font-light'>
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Side: 404 Content (60%) */}
            <div className='w-full lg:w-[60%] flex flex-col bg-white overflow-y-auto px-8 md:px-16 lg:px-24 py-12'>
                <div className={`flex-1 flex items-center justify-center py-20 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className='w-full max-w-[460px] text-center'>
                        <div className='mb-10 flex justify-center'>
                            <div className='p-6 bg-red-50 rounded-full'>
                                <IconError404 size={80} stroke={1} className='text-red-500' />
                            </div>
                        </div>

                        <h1 className='text-6xl font-black text-gray-900 mb-4 tracking-tight'>404</h1>
                        <h2 className='text-2xl font-bold text-gray-800 mb-6'>Oops! Page Not Found</h2>
                        <p className='text-gray-500 font-light mb-12 text-lg leading-relaxed'>
                            The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
                        </p>

                        <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
                            <button
                                onClick={() => navigate(-1)}
                                className='w-full sm:w-auto px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2'
                            >
                                <IconChevronLeft size={20} />
                                Go Back
                            </button>
                            <Link
                                to="/dashboard"
                                className='w-full sm:w-auto px-8 py-4 bg-primary hover:opacity-90 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-primary/20 flex items-center justify-center gap-2'
                            >
                                <IconHome size={20} />
                                Back to Home
                            </Link>
                        </div>
                    </div>
                </div>

                <div className={`mt-auto pt-10 flex justify-center transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className='flex items-center gap-3 opacity-70 hover:opacity-100 transition-all duration-500 py-4 px-8 rounded-full border border-gray-200 hover:border-primary/20 hover:bg-primary/5'>
                        <span className='text-[10px] uppercase tracking-[0.3em] font-medium text-gray-500'>Powered by</span>
                        <img src={hsclLogo2} alt='HSCL' className='h-6 w-auto object-contain brightness-95' />
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; transform: scale(1); }
                    50% { opacity: 0.2; transform: scale(1.1); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite ease-in-out;
                }
            `}</style>
        </div>
    )
}
